/**
 * Admin-tool: vragenlijst-preview.
 *
 * Laadt de actief gemarkeerde vragenlijst-versie via de publieke
 * `/api/vragenlijst/actief` endpoint en rendert de bestaande
 * `VragenlijstWizard` met `previewMode`. Bij Versturen wordt GEEN
 * API-POST gedaan — alleen een toast + reset van de wizard naar stap 1.
 *
 * Geen werknemer-koppeling, geen URL-params, geen localStorage.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, withRetry } from "../../lib/api";
import { VragenlijstWizard } from "../../components/VragenlijstWizard";
import { ErrorBanner, formatDateOnly } from "../../components/admin/AdminUI";

export default function PreviewVragenlijstPage() {
  const versieQ = useQuery({
    queryKey: ["vragenlijst-actief"],
    queryFn: async () => (await withRetry(() => api.get("/vragenlijst/actief"))).data,
    staleTime: 5 * 60 * 1000,
  });

  if (versieQ.isLoading) {
    return (
      <div data-testid="preview-vragenlijst-page" className="rounded-md border border-slate-200 bg-white p-8 text-center fulkra-tekst text-slate-400">
        Vragenlijst laden…
      </div>
    );
  }

  if (versieQ.error || !versieQ.data) {
    return (
      <div data-testid="preview-vragenlijst-page">
        <ErrorBanner error={versieQ.error || { message: "Kon de actieve vragenlijst niet laden." }} />
        <p className="fulkra-tekst text-slate-600">
          <Link to="/admin/instellingen" className="text-blue-600 underline" data-testid="preview-back-link">
            Terug naar Instellingen
          </Link>
        </p>
      </div>
    );
  }

  const versie = versieQ.data;
  const versieNaam = versie.versie_nummer || "onbekend";
  const versieDatum = versie.aangemaakt_op ? formatDateOnly(versie.aangemaakt_op) : "onbekend";

  return (
    <div data-testid="preview-vragenlijst-page">
      <div
        data-testid="preview-banner"
        className="sticky top-0 z-30 -mx-4 -mt-4 mb-4 border-b border-[color:var(--aandacht-rand)] bg-amber-100 px-4 py-3 fulkra-tekst text-amber-900 shadow-sm sm:-mx-6 sm:-mt-6 sm:px-6"
      >
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <span>
            <strong className="font-semibold">PREVIEW-MODUS</strong> — antwoorden worden niet opgeslagen.{" "}
            <span data-testid="preview-banner-versie">
              Versie: {versieNaam} ({versieDatum})
            </span>
          </span>
          <Link
            to="/admin/instellingen"
            className="text-amber-900 underline hover:text-amber-700"
            data-testid="preview-back-link"
          >
            Terug naar Instellingen
          </Link>
        </div>
      </div>

      <VragenlijstWizard previewMode skipAvg schema={versie.vragen_json} />
    </div>
  );
}
